import React from "react";
import { Box, Text } from "@commonsku/design-system";
import { Spinner } from "@commonsku/styles";
import { ASSIGNED_TASKS, MyTasksListKey, selectVisibleAssignedTasks, TASKS_DISPLAY_LIMIT, TaskNote } from "../../../redux/myTasks";
import { TASK_EMPTY_STATE_TEXT } from "./taskEmptyStateText";
import MyTasksSections from "./MyTasksSections";
import AssignedTasksSections from "./AssignedTasksSections";
import ViewAllTasksLink from "./ViewAllTasksLink";

export interface TasksMenuPanelProps {
  tab: MyTasksListKey;
  notes: TaskNote[];
  total: number;
  seenAt: string | null;
  loading: boolean;
  onToggleComplete: (noteId: string, complete: boolean) => void;
}

/** Renders one tab's task list: loading state, tab-specific section split, and the "View All" footer. */
const TasksMenuPanel = ({ tab, notes, total, seenAt, loading, onToggleComplete }: TasksMenuPanelProps) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" paddingY="6">
        <Spinner small />
      </Box>
    );
  }

  const isAssignedTasksTab = tab === ASSIGNED_TASKS;
  const Sections = isAssignedTasksTab ? AssignedTasksSections : MyTasksSections;

  // Assigned Tasks drops any note that's complete and already seen (see
  // selectVisibleAssignedTasks) - both remaining groups can end up empty
  // even though notes were fetched, so the empty state has to be decided
  // after that filtering, not on the raw fetched count.
  const isEmpty = isAssignedTasksTab
    ? Object.values(selectVisibleAssignedTasks(notes, seenAt)).every((group) => group.length === 0)
    : notes.length === 0;

  if (isEmpty) {
    return (
      <Text fontSize="sm" paddingX="2" paddingY="1.5">
        {TASK_EMPTY_STATE_TEXT[tab]}
      </Text>
    );
  }

  return (
    <Box>
      <Sections tab={tab} notes={notes} seenAt={seenAt} onToggleComplete={onToggleComplete} />
      {/* Assigned Tasks has no "view all" destination - the dashboard page
          this links to only shows tasks assigned to you, not ones you
          assigned to others - so it would be misleading here. */}
      {!isAssignedTasksTab && total > TASKS_DISPLAY_LIMIT && <ViewAllTasksLink total={total} />}
    </Box>
  );
};

export default TasksMenuPanel;
